import React, { useMemo, useRef, useState } from "react";
import { useWindowSize } from "./use-window-size";

/**
 * A hook to fill a container with the given element, it will aim to try and fit as many as it can without causing overflow.
 * It will slowly squeeze the given element to its minimum size.
 * @param Element The element to fill the container, should be able to accept a ref
 * @returns [the ref to be given to the container, an array of the given elements to be placed inside the container]
 * @example
 * const [containerRef, elements] = useContainerFill<HTMLDivElement, HTMLDivElement>(SomeForwardReffedDivElement);
 *
 * <div ref={containerRef} >
 *      {elements}
 * </div>
 */
export const useContainerFill = <TContainer extends HTMLElement, TElement extends HTMLElement>(
    Element: (props: React.RefAttributes<TElement>) => React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>>
): [React.MutableRefObject<TContainer>, React.ReactNode[]] => {
    // We want to listen to the screen width and heights
    const { screenHeight, screenWidth } = useWindowSize();

    // Save refs for the container the elements will be stored in and the initial element
    const containerRef = useRef<TContainer>();
    const singleElementRef = useRef<TElement>();

    const [prevElementsY, setPrevElementsY] = useState(0);

    const elements = useMemo(() => {
        // Its safe to assume we can always fit one element in the container, we will use this element to determine size
        const elements: React.ReactNode[] = [<Element ref={singleElementRef} />];
        let elementsX = 0;
        let elementsY = 0;
        if (containerRef.current && singleElementRef.current) {
            // Calculate how many we can fit based on the initially placed item
            // Overshoot by 1 to try and squeeze the element, otherwise it will always default to its max size
            elementsX = Math.floor(containerRef.current.clientWidth / singleElementRef.current.clientWidth) + 1;
            elementsY = Math.floor(containerRef.current.clientHeight / singleElementRef.current.clientHeight);
            elementsY = elementsY < 1 ? 1 : elementsY;
            // Check how much squeezing it has inceased our rows,
            // If we've only increased by 1 row its probably a result of the extra element, therefore fallback to a previous element
            if (elementsY >= prevElementsY + 2 || prevElementsY === 0) {
                // Save the new row count if it was 0 or if we've increased our rows by over 2
                setPrevElementsY(elementsY);
            } else {
                // if we're falling back to our saved rows, remove the extra node squeeze
                elementsY = prevElementsY;
                elementsX -= 1;
            }

            // Create the nodes in an array
            for (let index = 0; index < elementsX * elementsY - 1; index += 1) {
                elements.push(<Element />);
            }
        }
        return elements;
    }, [containerRef?.current, singleElementRef?.current, screenHeight, screenWidth]);

    // Return the array of elements to be placed and the container ref that should be applied to the container
    return [containerRef, elements];
};
